'use client'

import { ChevronDown } from 'lucide-react'
import { useState } from 'react'


interface Company {
  name: string
  taxRate: number
  type?: string
  effectiveFrom?: string
  effectiveTo?: string
}

interface HsTaxCBPGCountryGroupV2Props {
  countryName: string
  effectiveFrom: string
  effectiveTo: string
  minRate: number
  maxRate: number
  companies: Company[]
  defaultExpanded?: boolean
}

export default function HsTaxCBPGCountryGroupV2({
  countryName,
  effectiveFrom,
  effectiveTo,
  minRate,
  maxRate,
  companies,
  defaultExpanded = false,
}: HsTaxCBPGCountryGroupV2Props) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const rateDisplay =
    minRate === maxRate ? `${minRate}%` : `${minRate}% - ${maxRate}%`

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Accordion Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">
              {countryName}
            </h4>
            <p className="text-sm text-gray-500">
              Chính thức áp dụng thuế Chống bán phá giá cho hàng hóa có xuất xừ từ {countryName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-xl font-bold text-red-600">{rateDisplay}</div>
          <ChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
              }`}
          />
        </div>
      </div>

      {/* Accordion Body */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          {/* Info Cards */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <div className="text-sm font-semibold text-gray-500 mb-1">
                Biên độ thuế suất
              </div>
              <div className="text-lg font-bold text-red-600">
                {rateDisplay}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <div className="text-sm font-semibold text-gray-500 mb-1">
                Thời gian áp dụng
              </div>
              <div className="text-sm font-medium text-gray-900">
                {effectiveFrom} - {effectiveTo}
              </div>
            </div>
          </div>

          {/* Companies Table */}
          {companies.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Nhà sản xuất / Xuất khẩu
                    </th>
                    <th className="text-center py-3 px-4 font-medium text-gray-700 w-48">
                      Thời gian áp dụng
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700 w-32">
                      Mức thuế CBPG
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company, idx) => (
                    <tr
                      key={idx}
                      className={
                        idx < companies.length - 1
                          ? 'border-b border-gray-100'
                          : ''
                      }
                    >
                      <td className="py-3 px-4 text-gray-900">
                        {company.name}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-600 text-xs">
                        {company.effectiveFrom && company.effectiveTo ? (
                          <>
                            {company.effectiveFrom} - {company.effectiveTo}
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-red-600">
                        {company.taxRate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
